import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DsaiIngestionSource, type SplitConfirmationDto } from '@dealstack/shared/lib/generated';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { useActionToasts } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/useActionToasts';
import { preloadAnalysisWorkspace } from '../../AnalysisWorkspace/analysisWorkspaceChunk';
import type { SearchParamPatch } from '../../AnalysisWorkspace/workspaceUrlState.types';
import { applySearchParamPatch } from '../../AnalysisWorkspace/workspaceUrlState.utils';
import {
	useEstimateIngestionMutation,
	useStartIngestionMutation,
	useWarmUpIngestionMutation,
} from '../hooks/useIngestionQueries';
import { INGEST_PANEL, INGEST_SEARCH_PARAMS } from '../ingestion.constants';
import type { PageCapSplit } from '../ingestion.types';
import { isBatchPageCapExceeded, parseIngestPanel, toPageCapSplit } from '../ingestion.utils';
import { useIngestionUploads } from './useIngestionUploads';
import { useLibraryPicker } from './useLibraryPicker';
import { toAnalyseEstimateView } from './useExtractFromDocumentModalController.utils';
import type {
	AnalyseConfirmState,
	ExtractFromDocumentModalController,
	IngestSourcePanel,
	PageCapNoticeState,
	ShownIngestFrame,
	SourceFooterState,
	UseExtractFromDocumentModalControllerArgs,
} from './useExtractFromDocumentModalController.types';

/**
 * The modal's state lives in the URL (`?ingest=upload|library|processing&batch=`), so every frame is linkable.
 * Start waits for the user to confirm the server's estimate, and a start over the page cap for the split.
 */
export const useExtractFromDocumentModalController = ({
	dealId,
	personId,
}: UseExtractFromDocumentModalControllerArgs): ExtractFromDocumentModalController => {
	const tCapManage = useTranslationCapManage();
	const toasts = useActionToasts();
	const [searchParams, setSearchParams] = useSearchParams();
	const urlPanel = parseIngestPanel(searchParams.get(INGEST_SEARCH_PARAMS.panel));
	const urlBatchId = searchParams.get(INGEST_SEARCH_PARAMS.batch);
	// Processing follows a batch; without one the modal opens on the upload panel.
	const requestedPanel =
		urlPanel === INGEST_PANEL.PROCESSING && urlBatchId === null
			? INGEST_PANEL.UPLOAD
			: urlPanel;

	// Keep rendering the last frame while the modal animates out.
	const [shown, setShown] = useState<ShownIngestFrame>({
		panel: requestedPanel ?? INGEST_PANEL.UPLOAD,
		batchId: urlBatchId,
	});
	if (
		requestedPanel !== null &&
		(requestedPanel !== shown.panel || urlBatchId !== shown.batchId)
	) {
		setShown({ panel: requestedPanel, batchId: urlBatchId });
	}

	const opened = requestedPanel !== null;
	const choosing =
		requestedPanel === INGEST_PANEL.UPLOAD || requestedPanel === INGEST_PANEL.LIBRARY;
	// Warm the workspace chunk while the user uploads, so "Open analysis" never suspends.
	useEffect(() => {
		if (opened) {
			void preloadAnalysisWorkspace().catch(() => undefined);
		}
	}, [opened]);
	// Start the extraction pods and GPU readers while the user picks files: their cold start is most of the wait.
	const { mutate: warmUp } = useWarmUpIngestionMutation();
	useEffect(() => {
		if (choosing) {
			warmUp();
		}
	}, [choosing, warmUp]);

	const [isFullScreen, setIsFullScreen] = useState(false);
	const [capRefusal, setCapRefusal] = useState<{
		selectionKey: string;
		split: PageCapSplit;
	} | null>(null);

	const library = useLibraryPicker(dealId, requestedPanel === INGEST_PANEL.LIBRARY);
	const uploads = useIngestionUploads({
		dealId,
		enabled: requestedPanel === INGEST_PANEL.UPLOAD,
		onUseLibraryCopy: (documentId) => {
			library.addToSelection(documentId);
			showToastV2({
				title: tCapManage('dealstack_ai.ingest.upload.toast.library_copy_title'),
				message: tCapManage('dealstack_ai.ingest.upload.toast.library_copy_message'),
			});
		},
	});
	const startMutation = useStartIngestionMutation(dealId);
	const estimateMutation = useEstimateIngestionMutation();
	// The selection "Analyse documents" was pressed on; its estimate shows only while the selection is unchanged.
	const [confirmKey, setConfirmKey] = useState<string | null>(null);

	const updateParams = (patch: SearchParamPatch) =>
		setSearchParams((current) => applySearchParamPatch(current, patch), { replace: true });

	const closeConfirm = () => {
		setConfirmKey(null);
		estimateMutation.reset();
	};

	const onClose = () => {
		setIsFullScreen(false);
		setCapRefusal(null);
		closeConfirm();
		library.reset();
		updateParams({ [INGEST_SEARCH_PARAMS.panel]: null, [INGEST_SEARCH_PARAMS.batch]: null });
	};

	const isLibrary = shown.panel === INGEST_PANEL.LIBRARY;
	const source = isLibrary ? DsaiIngestionSource.LIBRARY : DsaiIngestionSource.UPLOAD;
	const documentIds = isLibrary ? library.selectedIds : uploads.readyFileIds;
	const selectionKey = `${source}:${documentIds.join(',')}`;
	const pageCapSplit = capRefusal?.selectionKey === selectionKey ? capRefusal.split : null;
	const isConfirming = confirmKey !== null && confirmKey === selectionKey;
	const selection = isLibrary
		? { source, libraryDocumentIds: documentIds }
		: { source, fileIds: documentIds };

	const start = (split?: SplitConfirmationDto) =>
		startMutation.mutate(
			{
				idempotencyKey: generateUUID(),
				...selection,
				...(personId === '' ? {} : { personId }),
				...(split === undefined ? {} : { split }),
			},
			{
				onSuccess: (started) => {
					if (started === undefined) {
						return;
					}
					setIsFullScreen(false);
					setCapRefusal(null);
					closeConfirm();
					library.reset();
					updateParams({
						[INGEST_SEARCH_PARAMS.panel]: INGEST_PANEL.PROCESSING,
						[INGEST_SEARCH_PARAMS.batch]: started.batchId,
					});
				},
				onError: (error) => {
					if (isBatchPageCapExceeded(error)) {
						closeConfirm();
						setCapRefusal({ selectionKey, split: toPageCapSplit(error) });
						return;
					}
					toasts.failed({
						title: tCapManage('dealstack_ai.ingest.start.toast.failed'),
						error,
					});
				},
			}
		);

	const requestEstimate = () => {
		setConfirmKey(selectionKey);
		estimateMutation.mutate(selection);
	};

	const confirm = ((): AnalyseConfirmState | null => {
		if (!isConfirming) {
			return null;
		}
		if (estimateMutation.isError) {
			return { status: 'failed', onRetry: requestEstimate, onCancel: closeConfirm };
		}
		if (estimateMutation.data === undefined) {
			return { status: 'estimating', onCancel: closeConfirm };
		}
		return {
			status: 'ready',
			view: toAnalyseEstimateView(estimateMutation.data),
			isStarting: startMutation.isPending,
			onConfirm: () => start(),
			onCancel: closeConfirm,
		};
	})();

	const sourcePages = isLibrary ? library.selectedPages : uploads.readyPages;
	const footer: SourceFooterState = {
		count: documentIds.length,
		unit: isLibrary ? 'selected' : 'uploaded',
		pages:
			isLibrary && (library.isCountingPages || library.isPageCountError) ? null : sourcePages,
		canAnalyse:
			documentIds.length > 0 &&
			pageCapSplit === null &&
			!isConfirming &&
			!startMutation.isPending &&
			(isLibrary
				? !library.isCountingPages && !library.isPageCountError
				: !uploads.isUploading),
		isStarting: startMutation.isPending && pageCapSplit === null && !isConfirming,
		onAnalyse: requestEstimate,
	};

	const pageCap: PageCapNoticeState | null =
		pageCapSplit === null
			? null
			: {
					...pageCapSplit,
					isStarting: startMutation.isPending,
					onStartSplit: () =>
						start({ pages: pageCapSplit.pages, batchCount: pageCapSplit.batchCount }),
					onDismiss: () => setCapRefusal(null),
				};

	return {
		opened,
		panel: shown.panel,
		batchId: shown.batchId,
		isFullScreen,
		onToggleFullScreen: () => setIsFullScreen((current) => !current),
		onClose,
		onSourceChange: (panel: IngestSourcePanel) =>
			updateParams({ [INGEST_SEARCH_PARAMS.panel]: panel }),
		uploads,
		library,
		footer,
		pageCap,
		confirm,
	};
};
